/**
 * Feature flag / A-B test client. Fetches the caller's variant assignments
 * from `GET /feature-flags` once per session and caches the result — see
 * docs/FEATURE_FLAGS.md.
 */

import { buildUrl } from '@callbridge/ui';
import { API_BASE } from './apiBase';

const ANONYMOUS_ID_KEY = 'cb_anon_id';

/** A stable per-browser ID used to bucket logged-out visitors. */
export function getAnonymousId(): string {
  let id = localStorage.getItem(ANONYMOUS_ID_KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(ANONYMOUS_ID_KEY, id);
  }
  return id;
}

let cachedFlags: Record<string, string> | null = null;
let inflight: Promise<Record<string, string>> | null = null;

/**
 * Fetches (and caches) the variant for every feature flag. Pass the current
 * access token so logged-in users are bucketed by user ID instead of their
 * anonymous ID. Resolves to `{}` on any error — callers should treat a
 * missing key as the control variant.
 */
export function fetchFeatureFlags(accessToken?: string | null): Promise<Record<string, string>> {
  if (cachedFlags) return Promise.resolve(cachedFlags);
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const url = buildUrl(API_BASE, '/feature-flags', {
        anonymousId: getAnonymousId(),
      });
      const headers: Record<string, string> = {};
      if (accessToken) headers.Authorization = `Bearer ${accessToken}`;

      const res = await fetch(url, { headers });
      if (!res.ok) return {};

      const json = (await res.json()) as { data?: { flags?: Record<string, string> } };
      cachedFlags = json.data?.flags ?? {};
      return cachedFlags;
    } catch {
      return {};
    } finally {
      inflight = null;
    }
  })();

  return inflight;
}
